import { connect } from 'react-redux';
import { get, keyBy, round, sumBy } from 'lodash';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';
import { switchCentralMode } from '@actions/user';

import { Button } from '@commons/utils/styledLibraryComponents';
import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { config } from '@src/config';

import { getLineGapFromAnalysisData } from '@src/routes/invoice/commons/utils/lineGapCalculation';
import invoiceService from '@services/invoice';

import utilsColumns from './utils/columns';
import utilsModals from './utils/modals';

import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import {
  Row,
  Container,
  RowContainer,
  TitleContainer,
  ButtonContainer,
  ContentContainer,
  ColumnsContainer,
  ButtonsContainer,
  TotalsContainer,
} from './styledComponents';
import useLocalStorage from '@hooks/useLocalStorage';

export const AssociatedOrders = (props) => {
  const {
    // Props
    invoiceControlList,
    invoiceTotal,
    lineGap,
    isInvoiceLocked,
    onLineGapRefresh,
    setHasAssociatedOrders,
    // Dispatch methods
    openGenericModal,
    refreshGenericModal,
    showSuccessMessage,
    showErrorMessage,
    switchCentralMode,
  } = props;

  const [isLoading, setIsLoading] = useState(true);

  const [associatedOrders, setAssociatedOrders] = useState([]);
  const [associatedOrdersTotal, setAssociatedOrdersTotal] = useState(null);

  const [ordersToBeAdded, setOrdersToBeAdded] = useState([]);

  const [isCentralMode, setIsCentralMode] = useLocalStorage('isCentralMode', false);

  const [columns] = useState(utilsColumns.get());

  // Retrieve invoiceControlList information
  useEffect(() => {
    if (!invoiceControlList.id) {
      return;
    }

    getAssociatedOrder(invoiceControlList.id);
  }, [invoiceControlList.id]);

  // Handle modal's refresh
  useEffect(() => {
    const params = utilsModals.getOrderMappingsModalConfig({
      invoiceControlList,
      associateOrders,
      ordersToBeAdded,
      setOrdersToBeAdded,
      alreadyMappedOrdersKeyById: keyBy(associatedOrders, 'id'),
    });

    refreshGenericModal(params);
  }, [ordersToBeAdded]);

  const getAssociatedOrder = async (invoiceId, { keepLoadingAfter = false } = {}) => {
    setIsLoading(true);

    try {
      const result = await invoiceService.getOrdersByInvoiceControlListId(invoiceId);

      setAssociatedOrders(result);
      setHasAssociatedOrders(!!result.length);
      setAssociatedOrdersTotal(sumBy(result, 'orderTotal'));

      return result;
    } catch {
      setAssociatedOrders([]);

      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.FETCH_ASSOCIATED_ORDERS_ERROR'));

      return [];
    } finally {
      if (!keepLoadingAfter) {
        setIsLoading(false);
      }
    }
  };

  const openModalNewAssociation = async () => {
    const params = utilsModals.getOrderMappingsModalConfig({
      invoiceControlList,
      associateOrders,
      ordersToBeAdded,
      setOrdersToBeAdded,
      alreadyMappedOrdersKeyById: keyBy(associatedOrders, 'id'),
    });

    openGenericModal(params);
  };

  /**
   * Handle the fact switching of referentiel mode (CC or PdV) according to store.isKitchen value
   */
  const openOrderNewTab = (reference, isStoreKitchen) => {
    // Need to switch mode
    if (isCentralMode !== isStoreKitchen) {
      setIsCentralMode(isStoreKitchen);
      switchCentralMode(isStoreKitchen);
    }

    if (isStoreKitchen) {
      window.open(`${config.baseAppUrl}central-kitchen-order/orders?search=${reference}`, '_blank');

      return;
    }

    window.open(`${config.baseAppUrl}order/orders?search=${reference}`, '_blank');
  };

  const triggerLineGapRefresh = async () => {
    try {
      const analysisData = await invoiceService.aggregatedAnalyseAndExport([invoiceControlList.id]);
      const lineGap = getLineGapFromAnalysisData(analysisData);
      onLineGapRefresh(lineGap);
      return true;
    } catch {
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.FETCH_ASSOCIATED_ORDERS_ERROR'));
      return false;
    }
  };

  const deleteOrderAssociation = async (orderId) => {
    setIsLoading(true);

    let hasError = false;

    try {
      await invoiceService.deleteOrderByInvoiceControlListId(invoiceControlList.id, orderId);
    } catch {
      hasError = true;
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.DISSOCIATE_ORDER_ERROR'));
    } finally {
      await getAssociatedOrder(invoiceControlList.id, { keepLoadingAfter: !hasError });
    }

    if (hasError) {
      return;
    }

    const refreshSucceeded = await triggerLineGapRefresh();
    setIsLoading(false);

    if (!refreshSucceeded) {
      return;
    }

    showSuccessMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.DISSOCIATE_ORDER_SUCCESS'));
  };

  const associateOrders = async () => {
    const orderIds = ordersToBeAdded.map(({ id }) => id);

    if (!orderIds.length) {
      return;
    }

    setIsLoading(true);

    let hasError = false;

    try {
      await invoiceService.addOrdersByInvoiceControlListId(invoiceControlList.id, orderIds);
    } catch {
      hasError = true;
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ASSOCIATE_ORDERS_ERROR'));
    } finally {
      await getAssociatedOrder(invoiceControlList.id, { keepLoadingAfter: !hasError });
    }

    if (hasError) {
      return;
    }

    const refreshSucceeded = await triggerLineGapRefresh();
    setIsLoading(false);

    if (!refreshSucceeded) {
      return;
    }

    showSuccessMessage(i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ASSOCIATE_ORDERS_SUCCESS'));
  };

  if (isLoading) {
    return (
      <Container>
        <TitleContainer>
          {i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ASSOCIATED_ORDERS')}
        </TitleContainer>
        <ContentContainer>
          <DeepsightComponentLoader />
        </ContentContainer>
        <ButtonContainer></ButtonContainer>
      </Container>
    );
  }

  return (
    <Container>
      <TitleContainer>
        <LeftRightSplitter
          left={
            <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.H2_INTER}>
              {i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ASSOCIATED_ORDERS')}
            </Text>
          }
          right={
            associatedOrders.length > 0 && (
              <TotalsContainer>
                <Text
                  color={ENUM_COLORS.DARKEST}
                  font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {`${i18next.t('GENERAL.TOTAL')} : ${
                    associatedOrdersTotal == null ? '-' : round(associatedOrdersTotal, 2)
                  }`}
                </Text>
                <Text
                  color={ENUM_COLORS.DARKEST}
                  font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {`${i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.TOTAL_GAP')} : ${
                    associatedOrdersTotal == null && invoiceTotal == null
                      ? '-'
                      : round((associatedOrdersTotal || null) - invoiceTotal || null, 2)
                  }`}
                </Text>
                <Text
                  color={ENUM_COLORS.DARKEST}
                  font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {`${i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.LINE_GAP')} : ${
                    lineGap != null ? round(lineGap, 2) : '-'
                  }`}
                </Text>
              </TotalsContainer>
            )
          }
          width={'100%'}
        />
      </TitleContainer>
      {associatedOrders.length > 0 && (
        <ContentContainer>
          <ColumnsContainer>
            {columns.map(({ name }, index) => (
              <Text
                color={ENUM_COLORS.DARKER}
                font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}
                key={`column-header-${index}`}
              >
                {name}
              </Text>
            ))}
          </ColumnsContainer>
          {associatedOrders.map((order, id) => (
            <RowContainer key={`list-container-${id}`}>
              <Row>{columns.map(({ render }) => render(order))}</Row>
              <ButtonsContainer>
                <Button
                  color={'inpulse-outline'}
                  handleClick={() => {
                    openOrderNewTab(
                      order.reference,
                      get(invoiceControlList, 'store.isKitchen', false),
                    );
                  }}
                  icon={'/images/inpulse/open-a-new-black-small.svg'}
                  iconCustomStyle={{ width: '16px', height: '16px' }}
                />
                <Button
                  color={'inpulse-grey'}
                  handleClick={() => deleteOrderAssociation(order.orderId)}
                  icon={'/images/inpulse/trash-white-small.svg'}
                  iconCustomStyle={{ width: '12px', height: '12px' }}
                />
              </ButtonsContainer>
            </RowContainer>
          ))}
        </ContentContainer>
      )}
      <ButtonContainer>
        <Button
          buttonCustomStyle={{ paddingLeft: '0px' }}
          color={'inpulse-outline'}
          handleClick={openModalNewAssociation}
          icon={'/images/inpulse/add-black-small.svg'}
          isDisabled={isInvoiceLocked}
          label={i18next.t('INVOICE.INVOICE_CONTROL_DETAILS.ASSOCIATE_AN_ORDER')}
          noBorder={true}
        />
      </ButtonContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params) => {
    dispatch(refreshGenericModal(params));
  },
  switchCentralMode: (centralMode) => {
    dispatch(switchCentralMode(centralMode));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AssociatedOrders);
